import type { ReactNode } from 'react';

/**
 * The frame every picker's list sits in, built like the availability panel.
 *
 * That panel reads as organised because it says what you are choosing before
 * it asks you to choose - a small label, "PAUSE FOR" - and puts the way out on
 * a ruled footer rather than among the choices. The select lists had only the
 * rows, so a list of six medicines and a list of six suppliers looked the same
 * until the names were read.
 *
 *   head    what this is, and how many there are
 *   status  a strip for "Searching" and "Loading more", so the rows never move
 *   menu    antd's own list, unchanged, on a white card inside the shell
 *   footer  keys, or the one action the list offers - add a new one
 *
 * The same card-inside-card as the rest of the app: the head and the footer
 * sit on the grey shell, the rows on the white card, so what you choose from
 * is the brightest thing in the popup.
 *
 * Use it as `popupRender={(menu) => <PickerPopup label="Suppliers" menu={menu} />}`.
 */
export function PickerPopup({
  label,
  count,
  note,
  status,
  footer,
  menu,
}: {
  label: string;
  /** How many rows, printed beside the label. A `+` suffix means more pages. */
  count?: number | string;
  /** A few words on the right of the head, in place of a count: "Nearest expiry first". */
  note?: string;
  status?: ReactNode;
  footer?: ReactNode;
  menu: ReactNode;
}) {
  return (
    <div className="picker">
      <div className="picker-head">
        <span className="picker-label">{label}</span>
        {note ? (
          <span className="picker-note">{note}</span>
        ) : count !== undefined ? (
          <span className="picker-count tabular">{count}</span>
        ) : null}
      </div>
      {status ? <div className="picker-status">{status}</div> : null}
      <div className="picker-list">{menu}</div>
      {footer ? <div className="picker-foot">{footer}</div> : null}
    </div>
  );
}

/** The three keys every list answers to, for the footer. */
export function PickerKeys({ children }: { children?: ReactNode }) {
  return (
    <>
      <span>
        <kbd>&uarr;</kbd>
        <kbd>&darr;</kbd> move
      </span>
      <span>
        <kbd>Enter</kbd> pick
      </span>
      <span>
        <kbd>Esc</kbd> close
      </span>
      {children}
    </>
  );
}
